
import Vue from "vue";
import { Component, Prop, Provide } from "vue-property-decorator";
import ElementStudioWidthAdjustmentBar from "./ElementStudioWidthAdjustmentBar.vue";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import ElementStudioTopbar from "./ElementStudioTopBar.vue";
import lodash from "lodash";

import {
  ElementStudioExpectedDataToLoad,
  CurrentlyEditingPreset,
  ElementStudioSaveLocation,
  PresetSection,
  ElementStudioSaveLocationWithName
} from "@/types/element-studio";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import defensiveCopy from "@/util/defensive-copy";
import ElementStudioTabsAndPanels from "@/components/ElementStudio/ElementStudioTabsAndPanels.vue";
import * as t from "io-ts";
import NotificationLogger from "@/lib/notifications/notifications";
import CodeInput from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import slugify from "slugify";
import { getMacroName } from "@/lib/elements/studio/element-to-php";
import {
  getItemNameFromSlug,
  getItemNamespaceFromSlug,
  getPathToAllControls,
  isValidItemName,
  logNotificationOfSameItem
} from "@/lib/elements/studio/misc";
import Icon from "@/components/UI/Icon.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import ControlSectionOrControl from "@/components/ElementStudio/ControlSectionOrControl.vue";
import { Control, ControlType } from "@/types/controls";
import { getEmptyElement } from "@/lib/elements/utils";
import { getBlankControl, getBlankControlSection } from "@/lib/controls/utils";
import Accordion from "@/components/UI/Accordion.vue";
import ElementStudioPropertyPaths from "@/components/ElementStudio/ElementStudioPropertyPaths.vue";
import ElementStudioDynPropPaths from "@/components/ElementStudio/ElementStudioDynPropPaths.vue";
import { configStore } from "@/store/store-accessor";
import { BuilderElement } from "@/types/elements";
import ToggleInput from "@/components/Reusable/CustomInputs/ToggleInput.vue";
import {
  getSlugFromNamespaceAndFilename,
  savePresetToServer
} from "@/lib/elements/studio/presets";

@Component({
  components: {
    ToggleInput,
    ElementStudioDynPropPaths,
    ElementStudioPropertyPaths,
    Accordion,
    ControlSectionOrControl,
    TextInput,
    Icon,
    CodeInput,
    ElementStudioTabsAndPanels,
    ElementStudioWidthAdjustmentBar,
    ElementStudioTopbar
  }
})
export default class ElementStudioPresets extends Vue {
  @Prop()
  value!: ElementStudioExpectedDataToLoad;

  lastSavedPreset: CurrentlyEditingPreset | false = false;
  currentlyEditingPreset: CurrentlyEditingPreset | false = false;

  saveInProgress = false;
  activeTab = "preset";
  accordionState = "";

  autoRefresh = true;

  toggleAccordion(key: string) {
    this.accordionState = this.accordionState === key ? "" : key;
  }

  /**
   * "Provide" is not reactive. We use state inside it, so it must be reactive
   * By giving it an object instead of a static function, we make it reactive
   *
   * https://github.com/soflyy/breakdance/issues/1550
   */
  reactiveContext() {
    return {
      replaceControls: (path: string, newControls: Control[]) => {
        if (!this.currentlyEditingPreset) return;

        const newEditingPreset = defensiveCopy(this.currentlyEditingPreset);
        const correctPath = this.getCorrectPathForPresetFromContextPath(path);

        lodash.set(
          newEditingPreset.presetData.preset,
          correctPath,
          newControls
        );

        this.updateCurrentlyEditingPreset(newEditingPreset);
      },
      pasteControl: (path: string, controlToPaste: Control) => {
        if (!this.currentlyEditingPreset) return;

        const newEditingPreset = defensiveCopy(this.currentlyEditingPreset);
        const correctPath = this.getCorrectPathForPresetFromContextPath(path);
        const currentControls = lodash.get(
          newEditingPreset.presetData.preset,
          correctPath
        );

        currentControls.push(controlToPaste);

        lodash.set(
          newEditingPreset.presetData.preset,
          correctPath,
          currentControls
        );

        this.updateCurrentlyEditingPreset(newEditingPreset);
      },
      addControl: (path: string) => {
        if (!this.currentlyEditingPreset) return;

        const newEditingPreset = defensiveCopy(this.currentlyEditingPreset);

        const correctPath = this.getCorrectPathForPresetFromContextPath(path);
        const parentControl = this.getParentControlOrControls(correctPath);

        if (!parentControl) return;

        if (Array.isArray(parentControl)) {
          parentControl.push(getBlankControl());
        } else {
          parentControl.children.push(getBlankControl());
        }

        lodash.set(
          newEditingPreset.presetData.preset,
          correctPath,
          parentControl
        );

        this.updateCurrentlyEditingPreset(newEditingPreset);
      },
      addPreset: (path: string) => {
        if (!this.currentlyEditingPreset) return;

        const presetSection = defensiveCopy(
          this.elementStudioContext.getPresetSection(
            "EssentialElements\\typography"
          )
        );

        if (!presetSection) return;

        presetSection.section.options.sectionOptions = {
          ...presetSection.section.options.sectionOptions,
          type: "popout"
        };

        const newEditingPreset = defensiveCopy(this.currentlyEditingPreset);
        const correctPath = this.getCorrectPathForPresetFromContextPath(path);
        const parentControl = this.getParentControlOrControls(correctPath);

        if (!parentControl) return;

        if (Array.isArray(parentControl)) {
          parentControl.push(presetSection.section);
        } else {
          parentControl.children.push(presetSection.section);
        }

        lodash.set(
          newEditingPreset.presetData.preset,
          correctPath,
          parentControl
        );

        this.updateCurrentlyEditingPreset(newEditingPreset);
      },
      deleteControl: (parentPath: string, index: number) => {
        if (!this.currentlyEditingPreset) return;

        const newEditingPreset = defensiveCopy(this.currentlyEditingPreset);
        const correctPath =
          this.getCorrectPathForPresetFromContextPath(parentPath);
        const parentControl = this.getParentControlOrControls(correctPath);

        if (!parentControl) return;

        if (Array.isArray(parentControl)) {
          parentControl.splice(index, 1);
        } else {
          parentControl.children.splice(index, 1);
        }

        lodash.set(
          newEditingPreset.presetData.preset,
          correctPath,
          parentControl
        );

        this.updateCurrentlyEditingPreset(newEditingPreset);
      },
      updateControl: (
        parentPath: string,
        index: number,
        newControl: Control
      ) => {
        if (!this.currentlyEditingPreset) return;
        const newEditingPreset = defensiveCopy(this.currentlyEditingPreset);

        if (parentPath === "presetSection") {
          newEditingPreset.presetData.preset.section = newControl;
        } else {
          const correctPath =
            this.getCorrectPathForPresetFromContextPath(parentPath);
          const parentControl = this.getParentControlOrControls(correctPath);

          if (!parentControl) return;

          if (Array.isArray(parentControl)) {
            parentControl.splice(index, 1, newControl);
          } else {
            parentControl.children.splice(index, 1, newControl);
          }

          lodash.set(
            newEditingPreset.presetData.preset,
            correctPath,
            parentControl
          );
        }

        this.updateCurrentlyEditingPreset(newEditingPreset);
      },
      getPresetSection: (sectionSlug: string) => {
        return this.value.presetSections.find(
          preset => preset.slug === sectionSlug
        );
      },
      getPresetSections: () => {
        return this.value.presetSections;
      },
      getPathsToAllControls: (
        controlTypes: ControlType[] = [],
        controlTypesToIgnore: ControlType[] = ["section"]
      ) => {
        if (!this.currentlyEditingPreset) return;

        return getPathToAllControls(
          this.currentlyEditingPreset.presetData.preset.section.children,
          controlTypes,
          controlTypesToIgnore,
          ""
        );
      },
      // This is here just to support the func and avoid any errors, but it doesn't affect anything in the preset
      // And shouldn't be called at all when editing it
      element: getEmptyElement,
      updateDependency: () => {},
      updateActions: () => {},
      updateElementValue: () => {},
      getElementStudioData: () => this.value,
      updateElementStudioData: (newData: ElementStudioExpectedDataToLoad) => {
        this.$emit("input", newData);
      }
    };
  }

  @Provide("context")
  elementStudioContext = this.reactiveContext();

  updateAutoRefresh(newAutoRefresh: boolean) {
    this.autoRefresh = newAutoRefresh;

    // Don't wait for a new change, update all elements
    if (newAutoRefresh && this.currentlyEditingPreset) {
      this.updateCurrentlyEditingPreset(this.currentlyEditingPreset);
    }
  }

  getCorrectPathForPresetFromContextPath(path: string) {
    return path.replace("control", "section");
  }

  getParentControlOrControls(path: string) {
    if (!this.currentlyEditingPreset) return;

    return defensiveCopy(
      lodash.get(this.currentlyEditingPreset.presetData.preset, path)
    ) as Control | Control[];
  }

  get codeHelpValue() {
    if (!this.currentlyEditingPreset) return "";

    return (
      this.currentlyEditingPreset.presetData.preset.section.options
        .sectionOptions?.preset?.codeHelp || ""
    );
  }

  get codeHelpPlaceholder() {
    if (!this.currentlyEditingPreset) return "";
    const potentialMacroName = getMacroName(
      getItemNamespaceFromSlug(this.currentlyEditingPreset.presetData.slug),
      this.currentPresetName
    );

    return `{{ macros.${potentialMacroName}(%%TWIG_PATH%%) }}`;
  }

  updateCodeHelp(newValue: string) {
    if (!this.currentlyEditingPreset) return;

    const newPreset = defensiveCopy(this.currentlyEditingPreset);

    lodash.set(
      newPreset,
      "presetData.preset.section.options.sectionOptions.preset.codeHelp",
      newValue
    );

    this.updateCurrentlyEditingPreset(newPreset);
  }

  get presetsSlug() {
    return this.value.presets.map(preset => preset.slug);
  }

  get allPaths() {
    return this.reactiveContext().getPathsToAllControls([], []);
  }

  get presetRelativePropertyPathsToWhitelistInFlatProps() {
    if (!this.currentlyEditingPreset) return [];

    return this.currentlyEditingPreset.presetData.preset.section.options
      .sectionOptions?.preset?.relativePropertyPathsToWhitelistInFlatProps;
  }

  updateElementValuePropPath(newValue: string[], propertyName: string) {
    if (!this.currentlyEditingPreset) return;

    const newCurrentlyEditingPreset = defensiveCopy(
      this.currentlyEditingPreset
    );

    lodash.set(
      newCurrentlyEditingPreset,
      `presetData.preset.section.options.sectionOptions.preset[${propertyName}]`,
      newValue
    );

    this.currentlyEditingPreset = newCurrentlyEditingPreset;
  }

  isValidItemName(newName: string) {
    return isValidItemName(newName);
  }

  updateCurrentlyEditingPreset(
    newCurrentlyEditingPreset: CurrentlyEditingPreset
  ) {
    // If the new preset comes from a context update, it won't have the section slug
    newCurrentlyEditingPreset.presetData.preset =
      this.getPresetWithRestoredSectionSlug(
        newCurrentlyEditingPreset.presetData.preset
      );

    this.currentlyEditingPreset = defensiveCopy(newCurrentlyEditingPreset);

    // Set the lastSavedPreset when opening a new element
    if (this.lastSavedPreset === false) {
      this.lastSavedPreset = defensiveCopy(newCurrentlyEditingPreset);
    }

    if (!this.autoRefresh) return;

    const allElementsWithModifiedPresetUpdated = Object.values(
      configStore.elements
    )
      .map(element => {
        if (!element) return element;

        element.controls.designSections = element.controls.designSections.map(
          this.updateSectionWithCurrentPresetSection
        );

        element.controls.contentSections = element.controls.contentSections.map(
          this.updateSectionWithCurrentPresetSection
        );

        element.controls.settingsSections =
          element.controls.settingsSections.map(
            this.updateSectionWithCurrentPresetSection
          );

        return element;
      })
      .filter((element): element is BuilderElement => element !== undefined);

    configStore.addElements(allElementsWithModifiedPresetUpdated);
  }

  get currentPresetWithoutPresetSlug() {
    if (!this.currentlyEditingPreset) return false;
    const newCurrentlyEditingPreset = defensiveCopy(
      this.currentlyEditingPreset
    );
    // Change it from being a preset to being a section so that we can edit its children
    // Inside ControlSectionOrControl
    if (
      newCurrentlyEditingPreset.presetData.preset.section.options.sectionOptions
        ?.preset?.slug
    ) {
      newCurrentlyEditingPreset.presetData.preset.section.options.sectionOptions.preset.slug =
        undefined;
    }
    return newCurrentlyEditingPreset;
  }

  updateSectionWithCurrentPresetSection(section: Control) {
    if (
      section.options.sectionOptions?.preset?.slug &&
      this.currentlyEditingPreset &&
      section.options.sectionOptions.preset.slug ===
        this.currentlyEditingPreset.presetData.slug
    ) {
      return defensiveCopy(
        this.currentlyEditingPreset.presetData.preset.section
      );
    }

    if (section.children.length) {
      const newSection = defensiveCopy(section);

      newSection.children = newSection.children.map(
        this.updateSectionWithCurrentPresetSection
      );

      return newSection;
    }

    return section;
  }

  filterOutCurrentSection(section: Control, slug: string) {
    if (
      section.options.sectionOptions?.preset?.slug &&
      section.options.sectionOptions.preset.slug === slug
    ) {
      return undefined;
    }

    if (section.children.length) {
      const newSection = defensiveCopy(section);

      newSection.children = newSection.children
        .map(s => this.filterOutCurrentSection(s, slug))
        .filter((s): s is Control => !!s);

      return newSection;
    }

    return section;
  }

  /**
   * We remove the slug from the section when first updating it to treat it as a control
   * This turns it back into a "preset section"
   * @param newPresetSection
   */
  getPresetWithRestoredSectionSlug(newPresetSection: PresetSection) {
    if (!this.currentlyEditingPreset) return newPresetSection;

    lodash.set(
      newPresetSection,
      "section.options.sectionOptions.preset.slug",
      this.currentlyEditingPreset.presetData.slug
    );

    return newPresetSection;
  }

  updateTab(newTab: string) {
    this.activeTab = newTab;
  }

  updatePresetSlug(newName: string) {
    if (!this.currentlyEditingPreset) return;

    const newEditingPreset = defensiveCopy(this.currentlyEditingPreset);

    newEditingPreset.presetData.slug = getSlugFromNamespaceAndFilename(
      getItemNamespaceFromSlug(this.currentlyEditingPreset.presetData.slug),
      newName
    );

    this.updateCurrentlyEditingPreset(newEditingPreset);
  }

  updatePreset(newPresetArg: CurrentlyEditingPreset["presetData"]) {
    const newPreset = defensiveCopy(newPresetArg);

    const presetsIndex = this.value.presets.findIndex(
      preset => preset.slug === newPreset.slug
    );
    const presetSectionsIndex = this.value.presetSections.findIndex(
      presetSection => presetSection.slug === newPreset.preset.slug
    );

    const newValue = defensiveCopy(this.value);

    if (presetsIndex === -1 && presetSectionsIndex === -1) {
      newValue.presets.push(newPreset);
      newValue.presetSections.push(newPreset.preset);
    } else {
      newValue.presets[presetsIndex] = newPreset;
      newValue.presetSections[presetSectionsIndex].section =
        newPreset.preset.section;
    }

    this.$emit("input", newValue);

    // Delete the old preset if the slug changed, and it's not a copy of the preset in a different save location
    if (
      this.lastSavedPreset &&
      this.lastSavedPreset.directoryPath &&
      newPreset.slug !== this.lastSavedPreset.presetData.slug &&
      newPreset.directoryPath === this.lastSavedPreset.directoryPath
    ) {
      this.deletePresetFromServer(this.lastSavedPreset);
    }
  }

  deletePresetAndRecompileTwigTemplates(deletedPresetSlug: string) {
    const deletedPresetType = getItemNameFromSlug(deletedPresetSlug);
    const presetSections = defensiveCopy(this.value.presetSections);
    const storeIndex = presetSections.findIndex(
      preset => preset.slug === deletedPresetType
    );
    const valueIndex = this.value.presets.findIndex(
      preset => preset.slug === deletedPresetSlug
    );

    if (storeIndex !== -1) {
      presetSections.splice(storeIndex, 1);
    }

    if (valueIndex !== -1) {
      const newValue = defensiveCopy(this.value);
      newValue.presets.splice(valueIndex, 1);

      this.$emit("input", newValue);
    }

    if (!this.autoRefresh) return;

    // Delete preset from all elements using it
    const allElementsWithDeletedPresetRemoved = Object.values(
      configStore.elements
    )
      .map(element => {
        if (!element) return element;

        element.controls.designSections = element.controls.designSections
          .map(section =>
            this.filterOutCurrentSection(section, deletedPresetSlug)
          )
          .filter((s): s is Control => !!s);

        element.controls.contentSections = element.controls.contentSections
          .map(section =>
            this.filterOutCurrentSection(section, deletedPresetSlug)
          )
          .filter((s): s is Control => !!s);

        element.controls.settingsSections = element.controls.settingsSections
          .map(section =>
            this.filterOutCurrentSection(section, deletedPresetSlug)
          )
          .filter((s): s is Control => !!s);

        return element;
      })
      .filter((element): element is BuilderElement => element !== undefined);

    configStore.addElements(allElementsWithDeletedPresetRemoved);
  }

  get currentPresetName() {
    if (!this.currentlyEditingPreset) return "";

    return getItemNameFromSlug(this.currentlyEditingPreset.presetData.slug);
  }

  get hasUnsavedChanges() {
    if (!this.currentlyEditingPreset || !this.lastSavedPreset) {
      return false;
    }

    return !lodash.isEqual(
      this.lastSavedPreset.presetData,
      this.currentlyEditingPreset.presetData
    );
  }

  deletePreset() {
    if (!this.currentlyEditingPreset) {
      return;
    }

    this.deletePresetFromServer(this.currentlyEditingPreset, () => {
      this.currentlyEditingPreset = false;
    });
  }

  async deletePresetFromServer(
    presetToDelete: CurrentlyEditingPreset,
    successCallback: () => void = () => {}
  ) {
    pipe(
      await makeWordPressAjaxRequest({
        action: "breakdance_delete_preset",
        dataToPost: {
          directoryPath: presetToDelete.directoryPath,
          filename: getItemNameFromSlug(presetToDelete.presetData.slug)
        },
        decoder: t.literal("success"),
        resultKey: "data"
      }),
      fold(
        () => {
          NotificationLogger.log.message("Failed to delete element");
        },
        () => {
          successCallback();

          this.deletePresetAndRecompileTwigTemplates(
            presetToDelete.presetData.slug
          );
        }
      )
    );
  }

  discardChanges() {
    if (this.lastSavedPreset && this.lastSavedPreset.presetData) {
      this.updateCurrentlyEditingPreset(this.lastSavedPreset);
      this.updatePreset(this.lastSavedPreset.presetData);
    }

    this.resetCurrentlyEditingPreset();
  }

  resetCurrentlyEditingPreset() {
    this.currentlyEditingPreset = false;
    this.lastSavedPreset = false;
    this.$emit("active", null);
  }

  openPreset(presetSlug: string) {
    const presetSaveLocation = defensiveCopy(
      this.value.presets.find(preset => preset.slug === presetSlug)
    );

    if (!presetSaveLocation) return;

    const preset = this.value.presetSections.find(
      preset => preset.slug === presetSlug
    );

    if (!preset) {
      NotificationLogger.log.message(`${presetSlug} couldn't be found`);

      return;
    }

    const activeSaveLocation = this.value.saveLocations.find(saveLocation =>
      presetSaveLocation.directoryPath.startsWith(saveLocation.directoryPath)
    );
    const onlyForAdvancedUsers = !!activeSaveLocation?.onlyForAdvancedUsers;

    this.updateCurrentlyEditingPreset({
      presetData: { ...presetSaveLocation, preset },
      directoryPath: presetSaveLocation.directoryPath,
      onlyForAdvancedUsers
    });
    this.$emit("active", "preset");
  }

  saveAs(saveLocation: ElementStudioSaveLocation) {
    if (!this.currentlyEditingPreset) return;

    this.saveToServer(
      this.currentlyEditingPreset.presetData.preset,
      saveLocation.directoryPath,
      this.currentPresetName,
      getSlugFromNamespaceAndFilename(
        saveLocation.namespace,
        this.currentPresetName
      ),
      saveLocation.onlyForAdvancedUsers
    );
  }

  saveNewItem(newItem: ElementStudioSaveLocationWithName) {
    const filename = slugify(newItem.name);
    const newSlug = getSlugFromNamespaceAndFilename(
      newItem.namespace,
      newItem.name
    );

    if (this.value.presets.find(preset => preset.slug === newSlug)) {
      logNotificationOfSameItem(this.value.saveLocations, newItem, "preset");

      return;
    }

    this.saveToServer(
      {
        slug: newSlug,
        section: getBlankControlSection()
      },
      newItem.directoryPath,
      filename,
      newSlug,
      newItem.onlyForAdvancedUsers
    );

    this.$emit("active", "preset");
  }

  saveOverride() {
    if (
      !this.currentlyEditingPreset ||
      !this.currentlyEditingPreset.directoryPath
    ) {
      return;
    }

    this.saveToServer(
      this.currentlyEditingPreset.presetData.preset,
      this.currentlyEditingPreset.directoryPath,
      this.currentPresetName,
      this.currentlyEditingPreset.presetData.slug,
      this.currentlyEditingPreset.onlyForAdvancedUsers
    );
  }

  async saveToServer(
    preset: PresetSection,
    directoryPath: string,
    filename: string,
    slug: string,
    onlyForAdvancedUsers: boolean
  ) {
    this.saveInProgress = true;

    pipe(
      await savePresetToServer(preset, directoryPath, filename, slug),
      fold(
        e => {
          console.error(e);
          NotificationLogger.log.message(
            "Failed to save element, check console"
          );
        },
        () => {
          const newPreset: CurrentlyEditingPreset = {
            presetData: {
              preset: defensiveCopy(preset),
              slug,
              directoryPath
            },
            directoryPath,
            onlyForAdvancedUsers
          };

          this.updateCurrentlyEditingPreset(newPreset);
          this.updatePreset(newPreset.presetData);
          this.lastSavedPreset = newPreset;
        }
      )
    );

    this.saveInProgress = false;
  }
}
